import { useQuery } from "@tanstack/react-query";
import { logger } from "@/lib/logger";
import { getHubBrowserClient } from "@/lib/supabase/typed-browser-client";
import { fetchReportTimeEntries } from "@/lib/reports/fetch-time-entries";
import type { ReportPagination } from "./report-helpers";
import { groupPayrollEntries } from "./payroll-grouping";
import type { PayrollUserMeta } from "./payroll-types";

export interface PayrollReportFilters {
  payPeriodStart?: string;
  payPeriodEnd?: string;
  employeeId?: string;
}

/**
 * Payroll report data — codegen-typed throughout.
 *
 * Reads denormalized `time_entries` columns via the shared typed fetch (no
 * nested-relation joins, no `any`), joins `app_users` for name/email/rate, and
 * groups into per-employee payroll data. Replaces the old loose `any` +
 * `unwrapRelation` query that could silently mis-read renamed columns.
 */
export function usePayrollReportData(
  filters: PayrollReportFilters,
  isClientUser: boolean,
  clientId?: string,
  _pagination?: ReportPagination,
  _isGroupingActive?: boolean
) {
  return useQuery({
    queryKey: ["payroll-reports", filters, isClientUser, clientId],
    queryFn: async () => {
      const supabase = getHubBrowserClient();

      const start = filters.payPeriodStart;
      const end = filters.payPeriodEnd;
      if (!start || !end) {
        return { data: { employees: [], summary: emptySummary() }, total: 0 };
      }

      let rows;
      try {
        rows = await fetchReportTimeEntries(supabase, {
          start,
          end,
          userId: filters.employeeId || undefined,
          // Payroll is employee/admin-only; scope to a company only if a client
          // context is ever passed.
          companyId: isClientUser && clientId ? clientId : undefined,
        });
      } catch (error) {
        logger.error("Error fetching payroll reports", error);
        throw new Error("Failed to fetch payroll data");
      }

      // Join app_users for display name + email + hourly rate.
      const userIds = Array.from(new Set(rows.map((r) => r.user_id).filter(Boolean)));
      const userMeta = new Map<string, PayrollUserMeta>();
      if (userIds.length > 0) {
        const { data: users, error: usersError } = await supabase
          .from("app_users")
          .select("id, email, first_name, last_name, hourly_rate")
          .in("id", userIds);
        if (usersError) {
          logger.error("Error fetching payroll user metadata", usersError);
          throw new Error("Failed to fetch payroll user metadata");
        }
        for (const u of users ?? []) {
          const name =
            `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || "Unknown User";
          userMeta.set(u.id, {
            id: u.id,
            name,
            email: u.email || "",
            hourlyRate: u.hourly_rate ?? undefined,
          });
        }
      }

      const employees = groupPayrollEntries(rows, userMeta);
      const totalPaySum = employees.reduce((sum, emp) => sum + (emp.totalPay || 0), 0);
      const summary = {
        totalHours: employees.reduce((sum, emp) => sum + emp.totalHours, 0),
        totalPay: totalPaySum || undefined,
        employeeCount: employees.length,
      };

      return { data: { employees, summary }, total: employees.length };
    },
    enabled: true,
  });
}

function emptySummary() {
  return { totalHours: 0, totalPay: undefined as number | undefined, employeeCount: 0 };
}
